import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLeavePrompt } from '@hooks/useLeavePrompt';
import LeaveConfirmModal from '@components/LeaveConfirmModal';
import { routes } from '@lib/routes';

const COUNTRIES = [
  'Portugal', 'Spain', 'France', 'Germany', 'Italy', 'Netherlands',
  'United Kingdom', 'United States', 'Canada', 'Brazil', 'Other',
];

export default function Payment() {
  const navigate = useNavigate();

  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [emails, setEmails] = useState(['', '']);
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [legalReport, setLegalReport] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const basePrice = 499;
  const legalFee = legalReport ? 500 : 0;
  const total = basePrice + legalFee;
  const deposit = 50;
  const dueOnCompletion = total - deposit;

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!fullName.trim()) next.fullName = 'Full name is required.';
    if (!emails[0] || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emails[0]))
      next.email0 = 'A valid email is required.';
    if (!address.trim()) next.address = 'Street address is required.';
    if (!city.trim()) next.city = 'City is required.';
    if (!country) next.country = 'Please select a country.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    localStorage.setItem('checkio_billing', JSON.stringify({
      fullName, companyName, emails: emails.filter(Boolean),
      address, city, postalCode, country, vatNumber, legalReport,
    }));
    // TODO: redirect to Stripe checkout
    navigate(routes.success);
  };

  const addEmail = () => setEmails([...emails, '']);
  const updateEmail = (i: number, val: string) => {
    const next = [...emails];
    next[i] = val;
    setEmails(next);
  };

  const inputCls = (hasError?: boolean) =>
    `w-full rounded-lg border bg-white px-4 py-3 text-sm text-veritas-navy placeholder-veritas-gray-400 focus:outline-none focus:ring-2 focus:ring-veritas-navy/20 ${hasError ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-veritas-navy/20'}`;

  const hasFormData = !!(fullName.trim() || companyName.trim() || emails.some(e => e.trim()) ||
    address.trim() || city.trim() || postalCode.trim() || country || vatNumber.trim());
  const { isBlocked, proceed, reset } = useLeavePrompt(hasFormData);

  return (
    <>
    <LeaveConfirmModal open={isBlocked} onStay={reset} onLeave={proceed} />
    <div className="min-h-screen bg-veritas-gray-50 font-sans text-veritas-navy flex flex-col">
      {/* Top bar */}
      <div className="h-12 bg-veritas-navy flex items-center justify-between px-4 sm:px-6 shrink-0">
        <a href="/" className="flex items-center justify-center shrink-0" aria-label="Home">
          <img src="/brand/veritas-inverse.svg" alt="Veritas" className="h-8 w-auto" />
        </a>
      </div>

      {/* Page content */}
      <main className="flex-1 w-full max-w-5xl mx-auto px-4 sm:px-6 pt-16 pb-12">
        {/* Page header */}
        <div className="flex flex-col gap-2 mb-8">
          <h1 className="veritas-heading-section">Payment Details</h1>
          <p className="veritas-body text-sm">Complete your purchase securely</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="flex flex-col lg:flex-row items-start gap-6">

            {/* Left column — single white card wrapping all form sections */}
            <div className="flex-1 min-w-0 flex flex-col gap-6 rounded-lg border border-veritas-gray-200 bg-white px-6 py-6 shadow-sm">

              {/* Contact Information */}
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-2 border-b border-veritas-gray-200 pb-2">
                  <svg className="w-4 h-4 text-veritas-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="font-sans font-semibold text-base text-veritas-navy">Contact Information</span>
                </div>

                <div className="flex flex-col gap-4">
                  {/* Full name */}
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-semibold text-veritas-gray-500">Full Name</label>
                    <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="John Doe" className={inputCls(!!errors.fullName)} />
                    {errors.fullName && <p className="text-xs text-red-600">{errors.fullName}</p>}
                  </div>

                  {/* Company name */}
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-semibold text-veritas-gray-500">
                      Company Name <span className="font-normal text-veritas-gray-400">(optional)</span>
                    </label>
                    <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Acme Inc." className={inputCls()} />
                  </div>

                  {/* Email addresses */}
                  <div className="flex flex-col gap-2">
                    <span className="text-xs font-semibold text-veritas-gray-500">Email Addresses</span>
                    {emails.map((email, i) => (
                      <input
                        key={i}
                        type="email"
                        value={email}
                        onChange={e => updateEmail(i, e.target.value)}
                        placeholder={i === 0 ? 'john@example.com' : 'billing@example.com'}
                        className={inputCls(i === 0 && !!errors.email0)}
                      />
                    ))}
                    {errors.email0 && <p className="text-xs text-red-600">{errors.email0}</p>}
                    {emails.length < 5 && (
                      <button
                        type="button"
                        onClick={addEmail}
                        className="flex items-center gap-2 text-xs font-semibold text-veritas-navy hover:opacity-70 transition-opacity self-end"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                        </svg>
                        Add another email
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Billing Details */}
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-2 border-b border-veritas-gray-200 pb-2">
                  <svg className="w-4 h-4 text-veritas-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="font-sans font-semibold text-base text-veritas-navy">Billing Details</span>
                </div>

                <div className="flex flex-col gap-4">
                  {/* Street address */}
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-semibold text-veritas-gray-500">Street Address</label>
                    <input type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder="123 Main St" className={inputCls(!!errors.address)} />
                    {errors.address && <p className="text-xs text-red-600">{errors.address}</p>}
                  </div>

                  {/* City + Postal */}
                  <div className="flex gap-4">
                    <div className="flex flex-1 flex-col gap-1">
                      <label className="text-xs font-semibold text-veritas-gray-500">City</label>
                      <input type="text" value={city} onChange={e => setCity(e.target.value)} placeholder="Lisbon" className={inputCls(!!errors.city)} />
                      {errors.city && <p className="text-xs text-red-600">{errors.city}</p>}
                    </div>
                    <div className="flex flex-1 flex-col gap-1">
                      <label className="text-xs font-semibold text-veritas-gray-500">Postal Code</label>
                      <input type="text" value={postalCode} onChange={e => setPostalCode(e.target.value)} placeholder="1000-001" className={inputCls()} />
                    </div>
                  </div>

                  {/* Country */}
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-semibold text-veritas-gray-500">Country</label>
                    <select
                      value={country}
                      onChange={e => setCountry(e.target.value)}
                      className={`w-full rounded-lg border bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-veritas-navy/20 ${errors.country ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : 'border-veritas-navy/20'} ${!country ? 'text-veritas-gray-400' : 'text-veritas-navy'}`}
                    >
                      <option value="" disabled>Select country</option>
                      {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {errors.country && <p className="text-xs text-red-600">{errors.country}</p>}
                  </div>

                  {/* VAT */}
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-semibold text-veritas-gray-500">
                      VAT / SIF / NIF <span className="font-normal text-veritas-gray-400">(optional)</span>
                    </label>
                    <input type="text" value={vatNumber} onChange={e => setVatNumber(e.target.value)} placeholder="e.g. PT507966990" className={inputCls()} />
                  </div>
                </div>
              </div>

              {/* Legal report add-on */}
              <div className="flex w-full items-start justify-between gap-4 rounded-lg border border-veritas-gray-200 bg-veritas-gray-50 px-6 py-5">
                <label className="flex items-start gap-3 cursor-pointer flex-1">
                  <input
                    type="checkbox"
                    checked={legalReport}
                    onChange={e => setLegalReport(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-veritas-navy/20 accent-veritas-navy shrink-0"
                  />
                  <span className="text-sm text-veritas-navy leading-snug">
                    I am interested in a legally signed report for court or judicial purposes
                  </span>
                </label>
                <span className="text-sm font-semibold text-veritas-navy shrink-0">+€500.00</span>
              </div>

              {/* Submit */}
              <button
                type="submit"
                className="h-12 w-full flex items-center justify-center gap-2 rounded-xl bg-veritas-navy text-white font-sans font-semibold text-sm hover:bg-veritas-navy/90 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Proceed to Pay &middot; €{deposit}
              </button>
            </div>

            {/* Right column — order summary */}
            <div className="w-full lg:w-80 shrink-0 flex flex-col gap-6 rounded-lg border border-veritas-gray-200 bg-white px-6 py-6 shadow-sm">
              <span className="font-sans font-semibold text-base text-veritas-navy">Order Summary</span>

              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-veritas-gray-500">Standard Report</span>
                  <span className="text-sm font-semibold text-veritas-navy">€{basePrice}.00</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-veritas-gray-500">Legal Report Fee</span>
                  <span className="text-sm font-semibold text-veritas-navy">€{legalFee}.00</span>
                </div>

                <div className="h-px w-full bg-veritas-gray-200" />

                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-veritas-navy">Total</span>
                  <span className="font-sans text-2xl font-bold text-veritas-navy">€{total}.00</span>
                </div>

                <div className="h-px w-full bg-veritas-gray-200" />

                <div className="flex items-center justify-between">
                  <span className="text-sm text-veritas-gray-500">Deposit (Due Now)</span>
                  <span className="text-sm font-semibold text-veritas-navy">€{deposit}.00</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-veritas-gray-500">Due Upon Completion</span>
                  <span className="text-sm font-semibold text-veritas-navy">€{dueOnCompletion}.00</span>
                </div>
              </div>

              {/* Secure badge */}
              <div className="flex flex-col gap-2 rounded-lg bg-veritas-gray-50 border border-veritas-gray-200 px-4 py-4">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-veritas-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span className="text-xs font-semibold text-veritas-navy">Secure Payment</span>
                </div>
                <span className="text-xs text-veritas-gray-500 leading-relaxed">
                  Your payment information is encrypted and secure. Powered by Stripe.
                </span>
              </div>
            </div>

          </div>
        </form>
      </main>
    </div>
    </>
  );
}
